import { Alert, Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { liveGameDocs } from '../../../helpers/games';
import SectionScoreCard from '../../ui/scoreboard/sectionScorecard';
import { useColorMode } from '../../../hooks/colorModeProvider';

const getTeamLabel = (team) => {
  if (!team) {
    return '-';
  }

  if (team.name) {
    return team.name;
  }

  if (Array.isArray(team.players) && team.players.length > 0) {
    return team.players.join(', ');
  }

  return '-';
};

const getColorToken = (color, fallback) => {
  if (!color) {
    return fallback;
  }

  const isHexColor = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color);
  return isHexColor ? fallback : color;
};

const getSetShots = (set, teamId, opponentId) => {
  const scores = Array.isArray(set?.scores) ? set.scores : [];

  return scores.reduce(
    (totals, end) => {
      totals.team += end?.teamScores?.[teamId] || 0;
      totals.opponent += end?.teamScores?.[opponentId] || 0;
      return totals;
    },
    { team: 0, opponent: 0 }
  );
};

const buildTeamsForGame = (game) => {
  const teamA = game?.teams?.teamA;
  const teamB = game?.teams?.teamB;

  if (!teamA?.id || !teamB?.id) {
    return [];
  }

  const sets = Array.isArray(game?.sets) ? game.sets : [];

  const createTeamRow = (team, opponent, fallbackColor, opponentFallbackColor) => {
    const setRows = sets.map((set) => {
      const totals = getSetShots(set, team.id, opponent.id);
      return {
        shotsWon: totals.team,
        shotsAgainst: totals.opponent
      };
    });

    const totalShotsWon = setRows.reduce((sum, set) => sum + set.shotsWon, 0);
    const totalShotsAgainst = setRows.reduce((sum, set) => sum + set.shotsAgainst, 0);

    return {
      teamId: team.id,
      teamName: getTeamLabel(team),
      teamAgainstName: getTeamLabel(opponent),
      game: game.id,
      colors: {
        fColor: getColorToken(team.color, fallbackColor),
        aColor: getColorToken(opponent.color, opponentFallbackColor)
      },
      sets: setRows,
      totalShotsWon,
      totalShotsAgainst,
      totalPoints: game?.teamTotals?.[team.id] || 0,
      net: totalShotsWon - totalShotsAgainst,
      playoff: {
        teamTotal: game?.playoff?.teamScores?.[team.id] || 0,
        totalAgainst: game?.playoff?.teamScores?.[opponent.id] || 0
      }
    };
  };

  return [createTeamRow(teamA, teamB, 'green', 'red'), createTeamRow(teamB, teamA, 'red', 'green')];
};

const SectionScorecardsOverlay = () => {
  const { eventId, sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const { mode, setMode } = useColorMode();
  const initialModeRef = useRef(mode);

  const sectionNameFromQuery = searchParams.get('sectionName') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectionName, setSectionName] = useState(sectionNameFromQuery || 'Section');
  const [games, setGames] = useState([]);
  const sectionHeader = sectionName || 'Section';

  useEffect(() => {
    const previousMode = initialModeRef.current;
    setMode('dark');

    return () => {
      setMode(previousMode);
    };
  }, [setMode]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const gamesRef = collection(store, 'games');
        const gamesQuery = query(gamesRef, where('section', '==', sectionId));
        const gamesSnap = await getDocs(gamesQuery);

        if (!isMounted) {
          return;
        }

        // Deleted games are already gone by the time this filter runs - the
        // helper above drops them - so this is only the event guard now.
        const sectionGames = liveGameDocs(gamesSnap)
          .map((gameDoc) => ({ id: gameDoc.id, ...gameDoc.data() }))
          .filter((game) => String(game.event) === String(eventId));

        let resolvedSectionName = sectionNameFromQuery;

        const sectionRef = doc(store, 'sections', sectionId);
        try {
          const sectionSnap = await getDoc(sectionRef);
          const sectionData = sectionSnap.exists() ? sectionSnap.data() : null;
          resolvedSectionName =
            sectionData?.name || sectionData?.sectionName || resolvedSectionName;
        } catch (sectionError) {
          // Ignore and keep resolving from other sources
        }

        if (!resolvedSectionName) {
          try {
            const eventSnap = await getDoc(doc(store, 'events', eventId));
            const eventData = eventSnap.exists() ? eventSnap.data() : null;
            const sectionFromEvent = (eventData?.sections || []).find(
              (section) => section.id === sectionId
            );
            resolvedSectionName =
              sectionFromEvent?.name || sectionFromEvent?.sectionName || resolvedSectionName;
          } catch (eventError) {
            // Ignore and fallback below
          }
        }

        setSectionName(resolvedSectionName || 'Section');

        setGames(sectionGames);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setError('Unable to load section scorecards.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [eventId, sectionId, sectionNameFromQuery]);

  const rounds = useMemo(() => {
    const grouped = new Map();

    games.forEach((game) => {
      const round = Number(game.round) || 1;
      const existing = grouped.get(round) || [];
      grouped.set(round, [...existing, ...buildTeamsForGame(game)]);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([round, teams]) => ({ round, teams }));
  }, [games]);

  return (
    <Box p={2}>
      <Stack spacing={2} alignItems="center">
        <Typography variant="h3" fontWeight={800} textAlign="center">
          {sectionHeader}
        </Typography>

        {loading && <Typography>Loading scorecards...</Typography>}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rounds.length === 0 && (
          <Alert severity="info">No round scorecards found for this section.</Alert>
        )}

        {!loading && !error && rounds.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={3} justifyContent="center" width="100%">
            {rounds.map((roundData) => (
              <SectionScoreCard
                key={roundData.round}
                section={{ id: sectionId, name: sectionName, sectionTotals: { teams: [] } }}
                teams={roundData.teams}
                round={roundData.round}
                isOverlay={true}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default SectionScorecardsOverlay;
